<script setup lang="ts">
import { BookOpen, ChevronLeft, ChevronRight, Layers3, Search, SlidersHorizontal, Zap } from "@lucide/vue";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ContentRow from "../components/ContentRow.vue";
import LoadingState from "../components/LoadingState.vue";
import { useContent } from "../composables/useContent";
import { countValues, getPrimaryLabel, getRecordSection, matchesRecord, sortRecords } from "../lib/content";
import type { ContentSection, ContentSort, ContentTopic, ContentType } from "../types/content";

const route = useRoute();
const router = useRouter();
const { records, loading, error, years } = useContent();
const pageSize = 18;

const query = ref(String(route.query.q ?? ""));
const section = ref<ContentSection | "all">((route.query.section as ContentSection) ?? "all");
const topic = ref<ContentTopic | "all">((route.query.topic as ContentTopic) ?? "all");
const category = ref(String(route.query.category ?? "all"));
const year = ref(String(route.query.year ?? "all"));
const type = ref<ContentType | "all">((route.query.type as ContentType) ?? "all");
const sort = ref<ContentSort>((route.query.sort as ContentSort) ?? "latest");
const page = ref(Math.max(1, Number(route.query.page ?? 1)));
const filtersOpen = ref(Boolean(route.query.category || route.query.year || route.query.type || route.query.sort));
const topicScroller = ref<HTMLElement | null>(null);
const canScrollTopicsBack = ref(false);
const canScrollTopicsForward = ref(false);

const filtered = computed(() => sortRecords(
  records.value.filter((record) => matchesRecord(record, {
    query: query.value,
    section: section.value,
    topic: topic.value,
    category: category.value,
    year: year.value,
    type: type.value,
  })).filter((record) => record.editorial_status === "published"),
  sort.value,
));
const categoryOptions = computed(() => countValues(records.value
  .filter((record) => matchesRecord(record, { section: section.value, topic: topic.value }))
  .map((record) => [getPrimaryLabel(record)])));
const pages = computed(() => Math.max(1, Math.ceil(filtered.value.length / pageSize)));
const visibleRecords = computed(() => filtered.value.slice((page.value - 1) * pageSize, page.value * pageSize));
const visibleStart = computed(() => visibleRecords.value.length ? (page.value - 1) * pageSize + 1 : 0);
const visibleEnd = computed(() => Math.min(page.value * pageSize, filtered.value.length));
const sectionCounts = computed(() => {
  const published = records.value.filter((record) => record.editorial_status === "published");
  return {
    all: published.length,
    knowledge: published.filter((record) => getRecordSection(record) === "knowledge").length,
    reviews: published.filter((record) => getRecordSection(record) === "reviews").length,
    resources: published.filter((record) => getRecordSection(record) === "resources").length,
  };
});
const advancedFilterCount = computed(() => [
  category.value !== "all",
  year.value !== "all",
  type.value !== "all",
  sort.value !== "latest",
].filter(Boolean).length);
const hasFilterAdjustments = computed(() => Boolean(query.value.trim()) || topic.value !== "all" || advancedFilterCount.value > 0);
const pageCopy = computed(() => {
  if (section.value === "knowledge") return { eyebrow: "知识库", title: "技术知识", description: "从 Linux、网络、安全、部署和开发问题出发，阅读已经完成审核的实践指南。", icon: BookOpen };
  if (section.value === "reviews") return { eyebrow: "证据与时效", title: "VPS 测评", description: "保留历史来源，但只有完成证据核对与独立评价后才会发布。", icon: Zap };
  if (section.value === "resources") return { eyebrow: "资源导航", title: "资源服务", description: "按云服务、运维与开发场景整理已核对的官方入口。", icon: Layers3 };
  return { eyebrow: "知识库", title: "全部已发布内容", description: "指南、VPS 方法与资源入口按主题集中浏览。", icon: BookOpen };
});
const topicOptions = computed(() => {
  if (section.value === "reviews") return [{ value: "regions", label: "地区索引" }];
  if (section.value !== "knowledge" && section.value !== "all") return [];
  return [
      { value: "linux", label: "Linux 与系统" },
      { value: "network", label: "网络与安全" },
      { value: "deploy", label: "建站与部署" },
      { value: "troubleshoot", label: "故障排查" },
      { value: "ai", label: "AI 与开发" },
      { value: "vps-guide", label: "VPS 指南" },
    ];
});

watch([query, section, topic, category, year, type, sort], () => {
  page.value = 1;
  syncRoute();
});
watch(page, () => syncRoute());
watch(pages, (value) => {
  if (page.value > value) page.value = value;
});
watch(topicOptions, async () => {
  await nextTick();
  topicScroller.value?.scrollTo({ left: 0 });
  updateTopicScrollState();
});
watch(() => route.query, (next) => {
  query.value = String(next.q ?? "");
  section.value = (next.section as ContentSection) ?? "all";
  topic.value = (next.topic as ContentTopic) ?? "all";
  category.value = String(next.category ?? "all");
  year.value = String(next.year ?? "all");
  type.value = (next.type as ContentType) ?? "all";
  sort.value = (next.sort as ContentSort) ?? "latest";
  page.value = Math.max(1, Number(next.page ?? 1));
  if (next.category || next.year || next.type || next.sort) filtersOpen.value = true;
}, { deep: true });

onMounted(() => {
  window.addEventListener("resize", updateTopicScrollState);
  void nextTick(updateTopicScrollState);
});
onBeforeUnmount(() => window.removeEventListener("resize", updateTopicScrollState));

function syncRoute() {
  const next: Record<string, string> = {};
  if (query.value.trim()) next.q = query.value.trim();
  if (section.value !== "all") next.section = section.value;
  if (topic.value !== "all") next.topic = topic.value;
  if (category.value !== "all") next.category = category.value;
  if (year.value !== "all") next.year = year.value;
  if (type.value !== "all") next.type = type.value;
  if (sort.value !== "latest") next.sort = sort.value;
  if (page.value > 1) next.page = String(page.value);
  if (JSON.stringify(next) !== JSON.stringify(route.query)) void router.replace({ query: next });
}

function selectSection(value: ContentSection | "all") {
  section.value = value;
  topic.value = "all";
  category.value = "all";
}

function clearFilters() {
  query.value = "";
  topic.value = "all";
  category.value = "all";
  year.value = "all";
  type.value = "all";
  sort.value = "latest";
}

function updateTopicScrollState() {
  const scroller = topicScroller.value;
  if (!scroller) return;
  canScrollTopicsBack.value = scroller.scrollLeft > 1;
  canScrollTopicsForward.value = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
}

function scrollTopics(direction: -1 | 1) {
  const scroller = topicScroller.value;
  if (!scroller) return;
  scroller.scrollBy({ left: direction * Math.max(150, scroller.clientWidth * 0.7), behavior: "smooth" });
}
</script>

<template>
  <section class="page-head library-head">
    <div class="page-width page-head-grid">
      <div><span>{{ pageCopy.eyebrow }}</span><h1>{{ pageCopy.title }}</h1><p>{{ pageCopy.description }}</p></div>
      <component :is="pageCopy.icon" :size="38" />
    </div>
    <div class="page-width library-head-nav">
      <div class="section-tabs" aria-label="内容栏目">
        <button type="button" :class="{ active: section === 'all' }" @click="selectSection('all')"><span>全部内容</span><small>{{ sectionCounts.all }}</small></button>
        <button type="button" :class="{ active: section === 'knowledge' }" @click="selectSection('knowledge')"><span>技术知识</span><small>{{ sectionCounts.knowledge }}</small></button>
        <button type="button" :class="{ active: section === 'reviews' }" @click="selectSection('reviews')"><span>VPS 测评</span><small>{{ sectionCounts.reviews }}</small></button>
        <button type="button" :class="{ active: section === 'resources' }" @click="selectSection('resources')"><span>资源服务</span><small>{{ sectionCounts.resources }}</small></button>
      </div>
    </div>
  </section>

  <section class="library-band band">
    <div class="page-width">
      <div class="filter-panel" :class="{ 'filters-open': filtersOpen }">
        <div class="filter-primary-row">
          <label class="filter-search"><Search :size="18" /><input v-model="query" type="search" aria-label="搜索已发布内容" placeholder="搜索标题、主题、地区或技术关键词" /></label>
          <button class="filter-disclosure" type="button" :aria-label="filtersOpen ? '收起更多筛选' : '展开更多筛选'" :aria-expanded="filtersOpen" @click="filtersOpen = !filtersOpen"><SlidersHorizontal :size="17" /><span>{{ filtersOpen ? "收起筛选" : "更多筛选" }}</span><small v-if="advancedFilterCount">{{ advancedFilterCount }}</small></button>
        </div>
        <div v-if="topicOptions.length" class="topic-navigation">
          <div ref="topicScroller" class="topic-chips" aria-label="主题筛选" @scroll="updateTopicScrollState">
            <button type="button" :class="{ active: topic === 'all' }" @click="topic = 'all'">全部主题</button>
            <button v-for="item in topicOptions" :key="item.value" type="button" :class="{ active: topic === item.value }" @click="topic = item.value as ContentTopic">{{ item.label }}</button>
          </div>
          <div class="topic-scroll-actions" aria-label="浏览更多主题">
            <button type="button" :disabled="!canScrollTopicsBack" aria-label="向前浏览主题" title="向前浏览主题" @click="scrollTopics(-1)"><ChevronLeft :size="17" /></button>
            <button type="button" :disabled="!canScrollTopicsForward" aria-label="向后浏览主题" title="向后浏览主题" @click="scrollTopics(1)"><ChevronRight :size="17" /></button>
          </div>
        </div>
        <div class="select-filters">
          <label><span>分类</span><select v-model="category"><option value="all">全部分类</option><option v-for="item in categoryOptions" :key="item.label" :value="item.label">{{ item.label }} ({{ item.count }})</option></select></label>
          <label><span>年份</span><select v-model="year"><option value="all">全部年份</option><option v-for="item in years" :key="item.label" :value="item.label">{{ item.label === 'undated' ? '日期待核' : item.label }} ({{ item.count }})</option></select></label>
          <label><span>类型</span><select v-model="type"><option value="all">全部类型</option><option value="article">文章</option><option value="page">专题页面</option></select></label>
          <label><span>排序</span><select v-model="sort"><option value="latest">最新发布</option><option value="oldest">最早发布</option><option value="title">标题排序</option></select></label>
        </div>
      </div>

      <div class="results-toolbar">
        <div><SlidersHorizontal :size="17" /><strong>{{ filtered.length.toLocaleString("zh-CN") }}</strong> 条结果 <span v-if="visibleRecords.length">· 当前 {{ visibleStart }}–{{ visibleEnd }}</span></div>
        <button v-if="hasFilterAdjustments" type="button" @click="clearFilters">重置筛选</button>
      </div>

      <LoadingState v-if="loading || error" :error="error" />
      <div v-else-if="visibleRecords.length" class="content-list library-list"><ContentRow v-for="(record, index) in visibleRecords" :key="record.id" :record="record" :position="visibleStart + index" /></div>
      <div v-else class="empty-state"><Search :size="24" /><strong>没有匹配的内容</strong><button type="button" @click="clearFilters">清除筛选</button></div>

      <nav v-if="pages > 1" class="pagination" aria-label="分页">
        <button type="button" :disabled="page <= 1" aria-label="上一页" @click="page--"><ChevronLeft :size="18" /></button>
        <span>第 <strong>{{ page }}</strong> / {{ pages }} 页</span>
        <button type="button" :disabled="page >= pages" aria-label="下一页" @click="page++"><ChevronRight :size="18" /></button>
      </nav>
    </div>
  </section>
</template>
